"use client";

// WO-GF-001-P1B-S32 (F186) — THE SITE-WIDE MOUNT for the small change-mountain dropdown.
//
// This is deliberately the SAME SHAPE as `MountainSelectLayer`, for the same reason S26 gave: the entry
// points live in different worlds (a React header on E3, a doc-scoped layer wiring CAPTURED markup on
// E4), and a control that has to be mounted per entry point will drift. The mount is a property of the
// surface; every entry point is a plain request on a native CustomEvent.
//
// The one addition over the select layer is the ANCHOR. This menu positions itself against the control
// that opened it, so the request carries that element. It is passed by reference through the event
// detail rather than by selector string — a selector would have to be kept in sync with two different
// markup sources, which is the drift this pattern exists to avoid.

import { useEffect, useState } from "react";
import MountainSwitchMenu from "./MountainSwitchMenu";
import type { MountainSelectMode } from "./MountainSelectModal";

export const MOUNTAIN_SWITCH_EVENT = "gf:mountain-switch";

/** Open the small dropdown from anywhere (React or plain DOM), anchored to `anchor`. */
export function openMountainSwitch(anchor: HTMLElement | null, mode: MountainSelectMode = null) {
  document.dispatchEvent(new CustomEvent(MOUNTAIN_SWITCH_EVENT, { detail: { anchor, mode } }));
}

// S34 · F200 — THE TOGGLE WAS FROZEN, NOT "DEAD AFTER ESCAPE", and the cause was an IMPURE UPDATER.
//
// This layer used to hold `open` / `anchor` / `mode` as three separate useState hooks and, because the
// toggle rule needs BOTH the previous anchor and the previous open flag, it reached the previous anchor
// by calling `setOpen(...)` from INSIDE the `setAnchor(prev => ...)` updater. A state updater must be a
// pure function of its argument (React: "Updating state based on the previous state" — updaters run
// during render and may be called more than once), and `apps/root/next.config.ts` sets
// `reactStrictMode: true`, so React DOUBLE-INVOKES the updater in dev precisely to surface impurity.
// Each invocation enqueued its own toggle onto `open`, and the pair cancelled:
//
//   first click   prev = null  → `!(wasOpen && prev === next)` is anchor-INSENSITIVE (`!(w && false)`
//                                = true both times) → the menu opens, which is why it worked ONCE
//   every click after, prev === next === the same badge → both updates reduce to `w => !w`
//                                → false→true→false, or true→false→true → THE STATE NEVER MOVES
//
// So the control was frozen in whatever state it was last left in, for the rest of the page's life. The
// founder met it as "dead after Escape" only because closing the R1a selector is what happens to leave
// it frozen CLOSED; the same freeze is visible with no selector involved at all.
// MEASURED at 0ef5e53 on /mountain-whistler, toggle clicked four times and nothing else touched:
//   rest=0 click1=1 click2=1 click3=1 click4=1  — it opens once and then ignores every later click.
//
// THE FIX IS THE SHAPE, NOT A PATCH ON THE SYMPTOM. One state object, one PURE updater: the rule that
// needs both previous values now reads them from the same `s`, so nothing has to be dispatched from
// inside anything else, and running the updater twice on the same input gives the same answer. That
// makes it correct for EVERY close path at once — Escape, the ✕, the overlay, "open the map" — because
// none of them are special-cased; they all just land on `open: false` and the next click reads it.
export default function MountainSwitchLayer() {
  const [state, setState] = useState<{ open: boolean; anchor: HTMLElement | null; mode: MountainSelectMode }>({
    open: false,
    anchor: null,
    mode: null,
  });

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ anchor?: HTMLElement | null; mode?: MountainSelectMode }>).detail;
      const anchor = detail?.anchor ?? null;
      const mode = detail?.mode ?? null;
      // re-requesting from the SAME anchor toggles, so the badge behaves like a menu button rather than
      // a control that can only ever open. Pure: same `s` in, same object out, no nested dispatch.
      setState((s) => ({ open: !(s.open && s.anchor === anchor), anchor, mode }));
    };
    document.addEventListener(MOUNTAIN_SWITCH_EVENT, onOpen);
    return () => document.removeEventListener(MOUNTAIN_SWITCH_EVENT, onOpen);
  }, []);

  return (
    <>
      <span data-mountain-switch-layer hidden />
      <MountainSwitchMenu
        open={state.open}
        anchor={state.anchor}
        mode={state.mode}
        // the anchor is deliberately KEPT on close. It is what the next click compares against, and it is
        // also the element Escape returns focus to (APG); clearing it here would re-arm the exact
        // anchor-insensitive first-click path this fix exists to stop relying on.
        onClose={() => setState((s) => (s.open ? { ...s, open: false } : s))}
      />
    </>
  );
}
